import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  ArrowLeft,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Tags,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Empty } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { commerceTableClassName } from '@/components/common/compact-table-date'
import { getErrorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import eventTagsApi from '@/services/event-tags'
import type { EventTag } from '@/types'

const tagSchema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  code: z.string().trim().optional(),
})

type TagFormData = z.infer<typeof tagSchema>

const slugify = (value: string) => value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

export default function TagsPage() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const tagKind = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('kind')?.trim().toLowerCase() === 'event' ? 'event' : null
  }, [location.search])
  const eventsListPath = withServiceInstanceId('/events', serviceInstanceId)

  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [editTag, setEditTag] = useState<EventTag | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<TagFormData>({
    resolver: zodResolver(tagSchema),
    defaultValues: { title: '', code: '' },
  })

  const {
    register: registerEdit,
    handleSubmit: handleEditSubmit,
    formState: { errors: editErrors },
    reset: resetEdit,
  } = useForm<TagFormData>({
    resolver: zodResolver(tagSchema),
    defaultValues: { title: '', code: '' },
  })

  const { data = [], isLoading, error } = useQuery({
    queryKey: ['event-tags', 'admin', serviceInstanceId ?? 'none'],
    queryFn: () => eventTagsApi.list({ include_inactive: true }),
    enabled: tagKind === 'event' && Boolean(serviceInstanceId),
  })

  const createMutation = useMutation({
    mutationFn: eventTagsApi.create,
    onSuccess: () => {
      toast.success('Tag created successfully')
      queryClient.invalidateQueries({ queryKey: ['event-tags'] })
      setCreateOpen(false)
      reset()
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TagFormData }) =>
      eventTagsApi.update(id, {
        title: payload.title.trim(),
        code: payload.code?.trim() || slugify(payload.title),
      }),
    onSuccess: () => {
      toast.success('Tag updated successfully')
      queryClient.invalidateQueries({ queryKey: ['event-tags'] })
      setEditTag(null)
      resetEdit()
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: eventTagsApi.deactivate,
    onSuccess: () => {
      toast.success('Tag deactivated successfully')
      queryClient.invalidateQueries({ queryKey: ['event-tags'] })
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const activateMutation = useMutation({
    mutationFn: eventTagsApi.activate,
    onSuccess: () => {
      toast.success('Tag activated successfully')
      queryClient.invalidateQueries({ queryKey: ['event-tags'] })
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  useEffect(() => {
    if (!editTag) {
      return
    }

    resetEdit({
      title: editTag.title,
      code: editTag.code,
    })
  }, [editTag, resetEdit])

  const filteredTags = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) {
      return data
    }

    return data.filter((tag) =>
      [tag.title, tag.code]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch)),
    )
  }, [data, search])

  const onSubmit = (formData: TagFormData) => {
    createMutation.mutate({
      title: formData.title.trim(),
      code: formData.code?.trim() || slugify(formData.title),
    })
  }

  const onEditSubmit = (formData: TagFormData) => {
    if (!editTag) {
      return
    }

    updateMutation.mutate({
      id: String(editTag.id),
      payload: formData,
    })
  }

  const toggleTagActive = (tag: EventTag) => {
    const id = String(tag.id)
    if (tag.active === false) {
      activateMutation.mutate(id)
      return
    }
    deactivateMutation.mutate(id)
  }

  if (tagKind !== 'event') {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-border/70 bg-card">
        <div className="text-center">
          <p className="mb-2 text-lg font-medium">Event tags only</p>
          <p className="mb-4 text-sm text-muted-foreground">
            Open this page from the Events workspace to manage event tags.
          </p>
          <Button variant="outline" asChild>
            <Link to={eventsListPath}>Back to events</Link>
          </Button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-destructive/20 bg-card">
        <div className="text-center">
          <p className="mb-2 text-destructive">Error loading tags</p>
          <p className="text-sm text-muted-foreground">{getErrorMessage(error)}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-2">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <Badge variant="outline" className="w-fit rounded-md border-violet-200 bg-violet-50 text-violet-700">
            Tag administration
          </Badge>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
              <Link to={eventsListPath}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Events
              </Link>
            </Button>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Event tags</h1>
            <Badge variant="secondary" className="rounded-md capitalize">event</Badge>
            <span className="rounded-full border border-border/70 bg-muted/30 px-2.5 py-1 text-xs text-muted-foreground">
              <Tags className="mr-1 inline h-3.5 w-3.5" />
              {data.length} total
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Manage tags used on events for filtering, grouping, and display.
          </p>
        </div>

        <Button className="rounded-lg" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create tag
        </Button>
      </section>

      <Card className="overflow-hidden rounded-[28px] border-white/70 bg-card/90 shadow-[0_20px_40px_-35px_rgba(15,23,42,0.55)]">
        <CardHeader className="space-y-0 border-b border-border/60 pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <CardTitle className="text-sm font-medium">Tags</CardTitle>
            <div className="relative min-w-[240px] lg:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by title or code"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-9 pl-9"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="rounded-lg border border-border/70 p-3">
                  <Skeleton className="h-3.5 w-48" />
                  <Skeleton className="mt-2 h-3.5 w-full" />
                </div>
              ))}
            </div>
          ) : filteredTags.length === 0 ? (
            <div className="py-16">
              <Empty>
                <div className="text-center">
                  <Tags className="mx-auto mb-3 h-8 w-8 text-muted-foreground/70" />
                  <p className="text-lg font-medium">No tags found</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {search.trim() ? 'Adjust your search or create a new tag.' : 'Create a tag to get started.'}
                  </p>
                  <Button className="mt-4 rounded-lg" onClick={() => setCreateOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create tag
                  </Button>
                </div>
              </Empty>
            </div>
          ) : (
            <div className={`overflow-x-auto ${commerceTableClassName}`}>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-4">Title</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="pr-4 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTags.map((tag) => {
                    const isActive = tag.active !== false

                    return (
                      <TableRow key={tag.id} className={cn(!isActive && 'opacity-70')}>
                        <TableCell className="pl-4 font-medium text-foreground">{tag.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-[11px] font-normal">
                            {tag.code}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={isActive
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 bg-slate-50 text-slate-600'}
                          >
                            {isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="pr-4 text-right" data-row-action="true">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" aria-label={`Actions for ${tag.title}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onClick={() => setEditTag(tag)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => toggleTagActive(tag)}
                                disabled={deactivateMutation.isPending || activateMutation.isPending}
                              >
                                {isActive ? (
                                  <>
                                    <PowerOff className="mr-2 h-4 w-4" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <Power className="mr-2 h-4 w-4" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open)
        if (!open) {
          reset()
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create event tag</DialogTitle>
            <DialogDescription>Add a tag that can be assigned to events.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <Label htmlFor="tag-title">Title</Label>
              <Input id="tag-title" {...register('title')} placeholder="e.g. Wedding" />
              {errors.title ? <p className="text-sm text-destructive">{errors.title.message}</p> : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="tag-code">Code (optional)</Label>
              <Input
                id="tag-code"
                {...register('code')}
                placeholder={watch('title') ? slugify(watch('title')) : 'auto-generated from title'}
              />
              {errors.code ? <p className="text-sm text-destructive">{errors.code.message}</p> : null}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                Create tag
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editTag)} onOpenChange={(open) => {
        if (!open) {
          setEditTag(null)
          resetEdit()
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit event tag</DialogTitle>
            <DialogDescription>Update the tag title or code.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleEditSubmit(onEditSubmit)}>
            <div className="space-y-2">
              <Label htmlFor="edit-tag-title">Title</Label>
              <Input id="edit-tag-title" {...registerEdit('title')} />
              {editErrors.title ? <p className="text-sm text-destructive">{editErrors.title.message}</p> : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tag-code">Code</Label>
              <Input id="edit-tag-code" {...registerEdit('code')} />
              {editErrors.code ? <p className="text-sm text-destructive">{editErrors.code.message}</p> : null}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditTag(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                Save changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
